/**
 * DocumentDashboard — Master Document Dashboard (DMS-12, drafts/57).
 * Owner: Claude (DMS workstream). Route: /documents/dashboard.
 * Read-only rollup of the controlled-document estate: lifecycle status mix,
 * periodic reviews due/overdue, controlled-print volume. Composes the real
 * GET /api/v1/documents/metrics (document.documents + periodic_review.reviews +
 * print.requests) — no new schema.
 */
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import { apiFetch } from '../../api/client';

interface DocMetrics {
  documents: {
    total: number; active: number; draft: number; inReview: number;
    effective: number; obsolete: number; byStatus: Record<string, number>;
    byClass?: Record<string, number>;
    byControlProfile?: Record<string, number>;
  };
  periodicReviews: { total: number; overdue: number; due30: number; due90: number };
  prints: { total: number; last30: number };
}

type Tone = 'danger' | 'warn' | 'ok' | 'default';

function Stat({ label, value, tone = 'default' }: { label: string; value: number | string; tone?: Tone }) {
  const color =
    tone === 'danger' ? '#B91C1C' : tone === 'warn' ? '#92400E' : tone === 'ok' ? '#0E9F7E' : 'var(--color-text, #1f2937)';
  return (
    <div
      role="figure"
      aria-label={`${label}: ${value}`}
      style={{
        border: '1px solid var(--color-border-subtle, #e5e7eb)',
        borderRadius: 12,
        padding: '16px 18px',
        background: 'var(--color-surface, #fff)',
      }}
    >
      <div style={{ fontSize: 13, color: 'var(--color-text-muted, #6B7989)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

const GRID = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 } as const;

export function DocumentDashboard() {
  const { t } = useTranslation();
  const q = useQuery<DocMetrics>({
    queryKey: ['documents', 'metrics'],
    queryFn: ({ signal }) => apiFetch<DocMetrics>('/api/v1/documents/metrics', { signal }),
    staleTime: 30_000,
  });

  return (
    <div style={{ padding: 'var(--space-6, 24px)', display: 'flex', flexDirection: 'column', gap: 24 }}>
      <PageHero
        eyebrow={t('dms.dashboard.eyebrow', { defaultValue: 'Documents' })}
        title={t('dms.dashboard.title', {
          defaultValue: 'Master Document Dashboard',
        })}
        subtitle={t('dms.dashboard.subtitle', {
          defaultValue:
            'Controlled-document estate: lifecycle status, periodic reviews, and controlled-print activity.',
        })}
      />

      {q.isLoading && <p role="status">{t('common.loading', { defaultValue: 'Loading…' })}</p>}
      {q.isError && (
        <p role="alert" style={{ color: '#B91C1C' }}>
          {t('dms.dashboard.error', { defaultValue: 'Could not load document metrics.' })}
        </p>
      )}

      {q.data && (
        <>
          <Card>
            <CardHeader>
              <h2 style={{ margin: 0, fontSize: 16 }}>{t('dms.dashboard.documents', { defaultValue: 'Documents' })}</h2>
            </CardHeader>
            <CardBody>
              <div style={GRID}>
                <Stat label={t('dms.dashboard.total', { defaultValue: 'Total' })} value={q.data.documents.total} />
                <Stat label={t('dms.dashboard.active', { defaultValue: 'Active' })} value={q.data.documents.active} tone="ok" />
                <Stat label={t('dms.dashboard.draft', { defaultValue: 'Draft' })} value={q.data.documents.draft} />
                <Stat label={t('dms.dashboard.inReview', { defaultValue: 'In Review' })} value={q.data.documents.inReview} tone="warn" />
                <Stat label={t('dms.dashboard.effective', { defaultValue: 'Effective' })} value={q.data.documents.effective} tone="ok" />
                <Stat label={t('dms.dashboard.obsolete', { defaultValue: 'Obsolete' })} value={q.data.documents.obsolete} />
              </div>
            </CardBody>
          </Card>

          {/* DMS-23 — 6-level classification hierarchy + GMP/general control profile. */}
          <Card>
            <CardHeader>
              <h2 style={{ margin: 0, fontSize: 16 }}>
                {t('dms.dashboard.classification', { defaultValue: 'Classification & control profile' })}
              </h2>
            </CardHeader>
            <CardBody>
              <div style={{ ...GRID, marginBottom: 16 }}>
                {([
                  ['QUALITY_MANUAL', 'Quality Manual'], ['POLICY', 'Policy'], ['SOP', 'SOP'],
                  ['WORK_INSTRUCTION', 'Work Instruction'], ['FORM', 'Form'], ['RECORD', 'Record'],
                ] as const).map(([k, label]) => (
                  <Stat key={k} label={label} value={q.data!.documents.byClass?.[k] ?? 0} />
                ))}
              </div>
              <div style={GRID}>
                <Stat
                  label={t('dms.dashboard.gmpControlled', { defaultValue: 'GMP-controlled' })}
                  value={q.data.documents.byControlProfile?.['GMP_CONTROLLED'] ?? 0}
                  tone="ok"
                />
                <Stat
                  label={t('dms.dashboard.generalControlled', { defaultValue: 'General-controlled (non-GMP)' })}
                  value={q.data.documents.byControlProfile?.['GENERAL_CONTROLLED'] ?? 0}
                />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h2 style={{ margin: 0, fontSize: 16 }}>
                {t('dms.dashboard.periodicReviews', { defaultValue: 'Periodic reviews' })}
              </h2>
            </CardHeader>
            <CardBody>
              <div style={GRID}>
                <Stat label={t('dms.dashboard.reviewsTotal', { defaultValue: 'Scheduled' })} value={q.data.periodicReviews.total} />
                <Stat
                  label={t('dms.dashboard.overdue', { defaultValue: 'Overdue' })}
                  value={q.data.periodicReviews.overdue}
                  tone={q.data.periodicReviews.overdue > 0 ? 'danger' : 'ok'}
                />
                <Stat
                  label={t('dms.dashboard.due30', { defaultValue: 'Due ≤ 30 days' })}
                  value={q.data.periodicReviews.due30}
                  tone={q.data.periodicReviews.due30 > 0 ? 'warn' : 'default'}
                />
                <Stat label={t('dms.dashboard.due90', { defaultValue: 'Due ≤ 90 days' })} value={q.data.periodicReviews.due90} />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h2 style={{ margin: 0, fontSize: 16 }}>
                {t('dms.dashboard.controlledPrints', { defaultValue: 'Controlled prints' })}
              </h2>
            </CardHeader>
            <CardBody>
              <div style={GRID}>
                <Stat label={t('dms.dashboard.printsTotal', { defaultValue: 'Total issued' })} value={q.data.prints.total} />
                <Stat label={t('dms.dashboard.printsLast30', { defaultValue: 'Last 30 days' })} value={q.data.prints.last30} />
              </div>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}
